/**
 * Generate contextual recommendations based on analysis results
 */

import type { ArtifactReport } from '../lib/analyzer'
import {
  getSizzleSeverity,
  getMuddinessSeverity,
  getClickSeverity,
  getDynamicRangeSeverity,
  getLufsSeverity,
} from './scoring'

export interface DetailedRecommendation {
  category: 'sizzle' | 'muddiness' | 'clicks' | 'dynamics' | 'loudness'
  severity: 'good' | 'moderate' | 'high' | 'critical'
  title: string
  description: string
  action: string
}

/**
 * Generate detailed recommendations with actions
 */
export function generateDetailedRecommendations(
  report: ArtifactReport
): DetailedRecommendation[] {
  const recommendations: DetailedRecommendation[] = []

  // Sizzle recommendations
  const sizzleSeverity = getSizzleSeverity(report.sizzleScore)
  if (sizzleSeverity.severity !== 'good') {
    recommendations.push({
      category: 'sizzle',
      severity: sizzleSeverity.severity,
      title: 'High-Frequency Harshness',
      description:
        'Harsh frequencies detected in the 5-8kHz range, which is common in AI-generated audio.',
      action:
        sizzleSeverity.severity === 'critical'
          ? 'Apply de-essing and high-shelf EQ cut at 6-8kHz (-3 to -6dB)'
          : 'Apply gentle high-shelf EQ cut at 6-8kHz (-1 to -3dB)',
    })
  }

  // Muddiness recommendations
  const muddinessSeverity = getMuddinessSeverity(report.muddinessScore)
  if (muddinessSeverity.severity !== 'good') {
    recommendations.push({
      category: 'muddiness',
      severity: muddinessSeverity.severity,
      title: 'Low-End Buildup',
      description:
        'Excessive sub-bass energy causing frequency masking and reduced clarity.',
      action:
        muddinessSeverity.severity === 'critical'
          ? 'Apply high-pass filter at 40-50Hz and reduce sub-bass by -4 to -6dB'
          : 'Apply gentle high-pass filter at 30-40Hz',
    })
  }

  // Click recommendations
  const clickSeverity = getClickSeverity(report.clickCount)
  if (clickSeverity.severity !== 'good') {
    recommendations.push({
      category: 'clicks',
      severity: clickSeverity.severity,
      title: 'Transient Artifacts',
      description: `${report.clickCount} click${report.clickCount !== 1 ? 's' : ''}/pop${report.clickCount !== 1 ? 's' : ''} detected as sudden transient spikes in the audio.`,
      action:
        clickSeverity.severity === 'critical'
          ? 'Use automatic click removal tool to clean all artifacts'
          : 'Use click removal or manually repair affected sections',
    })
  }

  // Dynamic range recommendations
  const drSeverity = getDynamicRangeSeverity(report.dynamicRangeDb)
  if (drSeverity.severity !== 'good') {
    recommendations.push({
      category: 'dynamics',
      severity: drSeverity.severity,
      title: 'Limited Dynamic Range',
      description: `Dynamic range of ${report.dynamicRangeDb.toFixed(1)} dB indicates ${drSeverity.severity === 'critical' ? 'severe' : ''} over-compression.`,
      action:
        drSeverity.severity === 'critical'
          ? 'Consider regenerating with less compression or using gentle mastering'
          : 'Use light compression settings to preserve dynamics',
    })
  }

  // Loudness recommendations
  const lufsSeverity = getLufsSeverity(report.lufs)
  if (lufsSeverity.severity !== 'good') {
    const isLoud = report.lufs > -14
    recommendations.push({
      category: 'loudness',
      severity: lufsSeverity.severity,
      title: isLoud ? 'Loudness Too High' : 'Loudness Too Low',
      description: isLoud
        ? `At ${report.lufs.toFixed(1)} LUFS, streaming platforms will turn your track down.`
        : `At ${report.lufs.toFixed(1)} LUFS, your track will sound quieter than other content.`,
      action: `Normalize to -14 LUFS for optimal streaming playback`,
    })
  }

  return recommendations
}

/**
 * Get a summary statement based on overall score
 */
export function getOverallSummary(score: number, report: ArtifactReport): string {
  if (score >= 80) {
    return 'Your AI-generated track is in great shape! It has minimal artifacts and is ready for release on streaming platforms.'
  }

  if (score >= 60) {
    const issues: string[] = []
    if (report.sizzleScore > 0.3) issues.push('high-frequency harshness')
    if (report.muddinessScore > 0.3) issues.push('low-end muddiness')
    if (report.clickCount > 5) issues.push('clicks')

    return `Your track has some minor issues (${issues.join(', ')}) that could benefit from professional mastering before release.`
  }

  if (score >= 40) {
    return 'Your track has several quality issues that should be addressed. AI music mastering can significantly improve the sound.'
  }

  return 'Your track has significant quality issues typical of AI-generated music. Professional mastering is strongly recommended before release.'
}

/**
 * Get quick fix summary for CTA
 */
export function getQuickFixSummary(report: ArtifactReport): string[] {
  const fixes: string[] = []

  if (report.sizzleScore > 0.3) {
    fixes.push('Reduce sizzle artifacts')
  }
  if (report.muddinessScore > 0.3) {
    fixes.push('Clean up muddy low-end')
  }
  if (report.clickCount > 0) {
    fixes.push(`Remove ${report.clickCount} click${report.clickCount !== 1 ? 's' : ''}`)
  }
  if (report.dynamicRangeDb < 8) {
    fixes.push('Improve dynamic range')
  }
  if (Math.abs(report.lufs + 14) > 2) {
    fixes.push('Optimize loudness for streaming')
  }

  return fixes
}
